import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import {
  OfficeHoursForMultiLocation,
  OfficeHoursWeekSchedule,
  Provider,
} from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import { ContactType_Enum, PersonStatus_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import dayjs from 'dayjs';
import { ScheduleTypes } from '@frontend/api-schedule';
import { IconName } from '@frontend/icons';
import { InsuranceTabUtilsTypes, InsuranceDetailsConstants } from '@frontend/insurance-tab';
import { getFullName } from '@frontend/name';
import { titleCase } from '@frontend/string';
import { ChipVariants } from '@frontend/design-system';
import type { FilterableCheckboxListItem } from '../../components/filterable-checkbox-list/use-filterable-checkbox-list';
import { TransformedLegacyPerson } from './components/PersonSelector/types';
import { getPersonContactInfoByType } from './utils';

export const transformToLegacyPerson = (person: Person) => {
  return {
    PreferredName: person.preferredName ?? '',
    FirstName: person.firstName ?? '',
    LastName: person.lastName ?? '',
    PersonID: person.personId,
    ContactInfo: person.contactInfo,
    Notes: person.notes ?? '',
    SourceID: person.sourceId ?? '',
    SourceTenantID: person.sourceTenantId ?? '',
    MobilePhone: getPersonContactInfoByType({
      contactInfo: person?.contactInfo,
      contactType: ContactType_Enum.PHONE_MOBILE,
    }),
    Email: getPersonContactInfoByType({ contactInfo: person?.contactInfo, contactType: ContactType_Enum.EMAIL }),
    HomePhone: getPersonContactInfoByType({
      contactInfo: person?.contactInfo,
      contactType: ContactType_Enum.PHONE_HOME,
    }),
    PMID: person.personPmid ?? '',
    PMDisplayId: person.personDisplayPmid ?? '',
    Gender: person.gender ?? '',
    Status: titleCase(PersonStatus_Enum[person.status || PersonStatus_Enum.UNSPECIFIED]),
    Birthdate: person.birthdate ?? '',
  } as unknown as TransformedLegacyPerson;
};

type GetFilteredAppointmentsProps = {
  appointmentsToFilter: Appointment[];
  providerList: Provider[];
  selectedAppointmentTypes?: string[];
  appointmentStatuses?: string[];
  selectedOperatoryList: string[];
};

export const getFilteredAppointments = ({
  appointmentsToFilter,
  providerList,
  selectedAppointmentTypes,
  appointmentStatuses,
  selectedOperatoryList,
}: GetFilteredAppointmentsProps): Appointment[] => {
  if (!appointmentsToFilter.length) return [];

  if (
    !providerList.length &&
    !selectedAppointmentTypes?.length &&
    !appointmentStatuses?.length &&
    !selectedOperatoryList.length
  ) {
    return appointmentsToFilter;
  }

  return appointmentsToFilter.filter((appointment) => {
    // Filter by appointment type
    if (selectedAppointmentTypes?.length && !selectedAppointmentTypes.includes(appointment.type ?? '')) {
      return false;
    }

    // Filter by operatory
    if (selectedOperatoryList?.length && !selectedOperatoryList.includes(appointment.operatory ?? '')) {
      return false;
    }

    // Filter by appointment statuses
    if (
      appointmentStatuses?.length &&
      !appointmentStatuses.some(
        (status) =>
          appointment.statusOfficeView?.toUpperCase().replaceAll('_', ' ') === status.toUpperCase().replaceAll('_', ' ')
      )
    ) {
      return false;
    }

    // Filter by provider
    if (providerList.length) {
      const appointmentPractitionerName = appointment.practitionerName?.trim();
      const matchesProvider = providerList.some((provider) => {
        const providerNames = [
          provider.publicDisplayName?.trim(),
          getFullName(provider),
          provider.resourceName?.trim(),
        ];
        const hasMatchingProvider =
          providerNames.includes(appointmentPractitionerName) && appointment.locationId === provider.locationId;

        const hasUnassignedProvider =
          !appointmentPractitionerName &&
          appointment.providerIds?.[0] === '' &&
          provider.id === `unassigned-${appointment.locationId}` &&
          provider.publicDisplayName?.trim() === 'unassigned';

        return hasMatchingProvider || hasUnassignedProvider;
      });

      if (!matchesProvider) return false;
    }

    return true;
  });
};

export const getUniqueOperatoryList = (locationIds: string[], defaultOperatoryList: ScheduleTypes.OperatoryType[]) => {
  const uniqueOperatories = new Map();
  // Remove duplicate operatories across all locations
  defaultOperatoryList.forEach((operatory) => {
    if (!operatory.value) return;
    const uniqueKey = `${operatory.key}-${operatory.display_name}`;
    // Check if the operatory is already in the map or if it belongs to the specified location. If it belongs to the specified location, we can add it even if it's a duplicate in a non-selected location.
    if (!uniqueOperatories.has(uniqueKey) || locationIds.includes(operatory.locationId)) {
      uniqueOperatories.set(uniqueKey, operatory);
    }
  });
  return Array.from(uniqueOperatories.values());
};

type GetIsValidEndTimeProps = {
  date: string;
  startTime: string;
  endTime: string;
};

export const isValidEndTime = ({ date, startTime, endTime }: GetIsValidEndTimeProps): boolean => {
  if (!date || !startTime || !endTime) return true;
  // Parse the date and time strings into dayjs objects
  const startDate = dayjs(`${date} ${startTime}`);
  const endDate = dayjs(`${date} ${endTime}`);

  // Check if the end time is before or the same as the start time
  if (endDate.isBefore(startDate) || endDate.isSame(startDate)) {
    return false;
  }

  return true;
};

export const getUniqueInsuranceStatuses = () => {
  const uniqueStatuses = new Map<string, InsuranceTabUtilsTypes.StatusInfo>();
  for (const value of InsuranceDetailsConstants.SCHEDULE_FILTER_INSURANCE_STATUS_MAPPING.values()) {
    const key = value.text.toLowerCase();
    if (!uniqueStatuses.has(key)) {
      uniqueStatuses.set(key, { ...value, text: key });
    }
  }
  return uniqueStatuses;
};

export type InsuranceStatusFilterItem = FilterableCheckboxListItem & {
  icon: IconName;
  variant: ChipVariants;
};

export const getInsuranceStatusesFilterList = (): InsuranceStatusFilterItem[] => {
  const uniqueStatuses = getUniqueInsuranceStatuses();
  return Array.from(uniqueStatuses.values()).map((status) => ({
    value: status.text.toLowerCase().replace(' ', '_') ?? '',
    label: status.text,
    icon: status.iconName,
    variant: status.variant,
  }));
};

type GetOfficeWorkingHoursParams = {
  officeHours: OfficeHoursForMultiLocation[];
  calendarDate: string;
  locationId: string;
};

export const getOfficeWorkingHours = ({ officeHours, calendarDate, locationId }: GetOfficeWorkingHoursParams) => {
  const officeHoursData =
    officeHours?.find((office) => office.locationId === locationId)?.officeHours ?? ([] as OfficeHoursWeekSchedule);
  const dayKey = dayjs(calendarDate).format('dddd').toLowerCase() as keyof OfficeHoursWeekSchedule;
  const officeHoursForDay = officeHoursData[dayKey] || [];

  const workingHoursForDay = officeHoursForDay.map((officeHour) => {
    const start = dayjs(officeHour.startTime, 'HH:mm');
    const end = dayjs(officeHour.endTime, 'HH:mm');
    const startTime = start.format('hh:mm A');
    const duration = end.diff(start, 'minute');
    return { startTime, duration };
  });

  return workingHoursForDay;
};
